/**
 * tax-service calls of the tax screens (T11-36 / T11-37 / T11-38, design/04 §13, design/06 §5):
 *
 * | what | endpoint | permission |
 * |---|---|---|
 * | settings in force today (+ history) | `GET /v1/admin/settings/tax` · `GET …/history` | tax.read / tax.settings |
 * | check a change + the ¥19,000 example (nothing saved) | `POST /v1/admin/settings/tax/validate` | tax.settings |
 * | save = a new version from a date (append-only) | `PATCH /v1/admin/settings/tax` (Idempotency-Key) | tax.settings |
 * | legal calendar (dates, days left, actions) | `GET /v1/admin/tax/legal-calendar` | tax.read |
 * | which rules apply today (免税 regime, overrides) | `GET /v1/admin/tax/legal-calendar/preview` | tax.read |
 * | owner notes on a legal date ("done on …") | `PATCH /v1/admin/tax/legal-calendar/{code}` | tax.settings |
 * | 免税 records / refund buckets | `GET /v1/admin/tax-free/records` · `GET /v1/admin/tax-free/refunds?status=` | tax.read / tax.taxfree |
 * | one record (buyer data only with kyc.view — audited) | `GET /v1/admin/tax-free/records/{id}` | tax.read |
 * | 税関確認 typed by staff · import a result file | `POST …/records/{id}/customs-result` · `POST …/customs-results/import?dryRun=` | tax.taxfree |
 * | refund of the 消費税相当額 · write-off · bank details | `POST …/records/{id}/refund` · `…/write-off` · `PUT …/refund-bank-details` | tax.taxfree / tax.settings / kyc.view |
 * | send again · 洗替 | `POST /v1/pos/tax-free/records/{id}/retransmit` · `…/cancel-and-resend` | tax.taxfree |
 * | 購入記録情報 files | `POST/GET /v1/admin/tax-free/exports` · `…/{id}/download?file=` · `…/{id}/mark-submitted` | tax.taxfree + kyc.view |
 * | monthly 免税販売 report | `GET /v1/admin/tax-free/reports/monthly?month=&format=` | tax.read |
 * | the same report as a PDF (media-service, T12-38) | media `POST /v1/admin/documents/tax-free-monthly-reports` (Idempotency-Key) → `GET /v1/admin/documents/{id}/download` | tax.read |
 * | 輸出免税 evidence | `GET /v1/admin/tax/export-evidence[?status=]` · `…/{orderId}` · `POST …/{orderId}/items` · `DELETE …/items/{id}` | tax.read / tax.settings or orders.fulfill |
 *
 * Every amount, date and status is tax-service's — the admin shows them and never calculates tax.
 */
import {
  createMediaClient,
  createTaxClient,
  type MediaPaths,
  type ServiceClientOptions,
  type TaxPaths,
} from "@tcg/api-client";
import { useMemo } from "react";

import { apiErrorFrom, unwrap } from "@/lib/api/errors";
import { useClientOptions } from "@/lib/api/use-api";

type Json<T> = T extends { content: { "application/json": infer B } } ? B : never;
type Body<T> = T extends { requestBody: { content: { "application/json": infer B } } }
  ? B
  : T extends { requestBody?: { content: { "application/json": infer B } } }
    ? B
    : never;
type P = TaxPaths;

export type TaxSetting = Json<P["/v1/admin/settings/tax"]["get"]["responses"][200]>;
export type TaxSettingPatch = Body<P["/v1/admin/settings/tax"]["patch"]>;
export type TaxSettingSaved = Json<P["/v1/admin/settings/tax"]["patch"]["responses"][200]>;
export type TaxValidation = Json<P["/v1/admin/settings/tax/validate"]["post"]["responses"][200]>;
export type TaxPreview = TaxValidation["preview"];
export type SettingIssue = TaxValidation["errors"][number];
export type TaxHistory = Json<P["/v1/admin/settings/tax/history"]["get"]["responses"][200]>;
export type LegalCalendar = Json<P["/v1/admin/tax/legal-calendar"]["get"]["responses"][200]>;
export type LegalEvent = LegalCalendar["data"][number];
export type LegalEventPatch = Body<P["/v1/admin/tax/legal-calendar/{code}"]["patch"]>;
export type RulesPreview = Json<P["/v1/admin/tax/legal-calendar/preview"]["get"]["responses"][200]>;

export type TaxFreeRecord = Json<
  P["/v1/admin/tax-free/records/{record_id}"]["get"]["responses"][200]
>;
export type TaxFreePage = Json<P["/v1/admin/tax-free/records"]["get"]["responses"][200]>;
export type RefundList = Json<P["/v1/admin/tax-free/refunds"]["get"]["responses"][200]>;
export type RefundBucket = NonNullable<
  P["/v1/admin/tax-free/refunds"]["get"]["parameters"]["query"]
>["status"];
export type CustomsResultIn = Body<
  P["/v1/admin/tax-free/records/{record_id}/customs-result"]["post"]
>;
export type TaxFreeRefundMethod = Body<
  P["/v1/admin/tax-free/records/{record_id}/refund"]["post"]
>["method"];
export type CustomsImport = Json<
  P["/v1/admin/tax-free/customs-results/import"]["post"]["responses"][200]
>;
export type ExportBatch = Json<P["/v1/admin/tax-free/exports"]["post"]["responses"][201]>;
export type ExportBatchIn = Body<P["/v1/admin/tax-free/exports"]["post"]>;
export type ExportFileKind = ExportBatch["files"][number]["kind"];
export type TaxFreeMonthly = Json<P["/v1/admin/tax-free/reports/monthly"]["get"]["responses"][200]>;
/** media-service's document of the monthly report PDF (T12-38): number, version, supersedes … */
export type ReportDocument = Json<
  MediaPaths["/v1/admin/documents/tax-free-monthly-reports"]["post"]["responses"][201]
>;
export type ReportLocale = NonNullable<
  Body<MediaPaths["/v1/admin/documents/tax-free-monthly-reports"]["post"]>["locale"]
>;

export type ExportEvidence = Json<
  P["/v1/admin/tax/export-evidence/{order_id}"]["get"]["responses"][200]
>;
export type ExportEvidencePage = Json<P["/v1/admin/tax/export-evidence"]["get"]["responses"][200]>;
export type EvidenceKind = ExportEvidence["requiredKinds"][number];
export type EvidenceStatus = ExportEvidence["status"];
export type EvidenceItemIn = Body<P["/v1/admin/tax/export-evidence/{order_id}/items"]["post"]>;

export interface TaxFreeQuery {
  status?: string | null;
  from?: string | null;
  to?: string | null;
  q?: string | null;
  limit?: number;
  cursor?: string | null;
}

export interface TaxAdminApi {
  /** `at` = the version in force on that JST day (default today). */
  settings(at?: string | null, signal?: AbortSignal): Promise<TaxSetting>;
  history(signal?: AbortSignal): Promise<TaxHistory>;
  validate(patch: TaxSettingPatch, signal?: AbortSignal): Promise<TaxValidation>;
  save(patch: TaxSettingPatch, key: string): Promise<TaxSettingSaved>;
  calendar(signal?: AbortSignal): Promise<LegalCalendar>;
  rules(date: string | null, signal?: AbortSignal): Promise<RulesPreview>;
  patchCalendar(code: string, body: LegalEventPatch, key: string): Promise<LegalEvent>;

  records(query: TaxFreeQuery, signal?: AbortSignal): Promise<TaxFreePage>;
  record(id: string, signal?: AbortSignal): Promise<TaxFreeRecord>;
  refunds(
    status: RefundBucket | null,
    query: { limit?: number; cursor?: string | null },
    signal?: AbortSignal,
  ): Promise<RefundList>;
  customsResult(id: string, body: CustomsResultIn, key: string): Promise<TaxFreeRecord>;
  refund(id: string, method: TaxFreeRefundMethod, key: string): Promise<TaxFreeRecord>;
  writeOff(id: string, reason: string, key: string): Promise<TaxFreeRecord>;
  bankDetails(id: string, details: Record<string, string>, key: string): Promise<TaxFreeRecord>;
  retransmit(id: string, key: string): Promise<TaxFreeRecord>;
  cancelAndResend(
    id: string,
    body: { remainingLineIds: string[] | null; reason: string | null },
    key: string,
  ): Promise<TaxFreeRecord>;
  exports(signal?: AbortSignal): Promise<ExportBatch[]>;
  createExport(body: ExportBatchIn, key: string): Promise<ExportBatch>;
  downloadExport(id: string, file: ExportFileKind): Promise<Blob>;
  markSubmitted(
    id: string,
    body: { reference: string; submittedAt?: string | null; note?: string | null },
    key: string,
  ): Promise<ExportBatch>;
  importCustoms(csv: string, dryRun: boolean, key: string): Promise<CustomsImport>;
  monthly(month: string, signal?: AbortSignal): Promise<TaxFreeMonthly>;
  monthlyCsv(month: string): Promise<Blob>;
  /** The PDF from the service: same figures → the same document, changed → a new version. */
  monthlyPdf(
    month: string,
    locale: ReportLocale,
    key: string,
  ): Promise<{ document: ReportDocument; pdf: Blob }>;

  evidence(orderId: string, signal?: AbortSignal): Promise<ExportEvidence>;
  evidenceQueue(
    query: {
      status?: EvidenceStatus | null;
      olderThanDays?: number | null;
      limit?: number;
      cursor?: string | null;
    },
    signal?: AbortSignal,
  ): Promise<ExportEvidencePage>;
  addEvidence(orderId: string, body: EvidenceItemIn, key: string): Promise<ExportEvidence>;
  removeEvidence(orderId: string, itemId: string, key: string): Promise<ExportEvidence>;
}

const idem = (key: string) => ({ "Idempotency-Key": key });

export function createTaxAdminApi(options: ServiceClientOptions): TaxAdminApi {
  const tax = createTaxClient(options);
  const media = createMediaClient(options);
  const fetcher = options.fetch ?? fetch;
  const root = options.baseUrl.replace(/\/+$/, "");
  const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});
  const blob = async (
    request: Promise<{ data?: unknown; error?: unknown; response: Response }>,
  ) => {
    const result = await request;
    if (!result.response.ok || !result.data) {
      throw apiErrorFrom(result.response, result.error);
    }
    return result.data as Blob;
  };
  return {
    settings: (at, signal) =>
      unwrap(
        tax.GET("/v1/admin/settings/tax", {
          params: { query: at ? { at } : {} },
          ...withSignal(signal),
        }),
      ),
    history: (signal) =>
      unwrap(tax.GET("/v1/admin/settings/tax/history", { ...withSignal(signal) })),
    validate: (patch, signal) =>
      unwrap(tax.POST("/v1/admin/settings/tax/validate", { body: patch, ...withSignal(signal) })),
    save: (patch, key) =>
      unwrap(tax.PATCH("/v1/admin/settings/tax", { params: { header: idem(key) }, body: patch })),
    calendar: (signal) =>
      unwrap(tax.GET("/v1/admin/tax/legal-calendar", { ...withSignal(signal) })),
    rules: (date, signal) =>
      unwrap(
        tax.GET("/v1/admin/tax/legal-calendar/preview", {
          params: { query: date ? { date } : {} },
          ...withSignal(signal),
        }),
      ),
    patchCalendar: (code, body, key) =>
      unwrap(
        tax.PATCH("/v1/admin/tax/legal-calendar/{code}", {
          params: { path: { code }, header: idem(key) },
          body,
        }),
      ),

    records: (query, signal) =>
      unwrap(
        tax.GET("/v1/admin/tax-free/records", {
          params: {
            query: {
              limit: query.limit ?? 100,
              ...(query.status ? { status: query.status } : {}),
              ...(query.from ? { from: query.from } : {}),
              ...(query.to ? { to: query.to } : {}),
              ...(query.q ? { q: query.q } : {}),
              ...(query.cursor ? { cursor: query.cursor } : {}),
            },
          },
          ...withSignal(signal),
        }),
      ),
    record: (id, signal) =>
      unwrap(
        tax.GET("/v1/admin/tax-free/records/{record_id}", {
          params: { path: { record_id: id } },
          ...withSignal(signal),
        }),
      ),
    refunds: (status, query, signal) =>
      unwrap(
        tax.GET("/v1/admin/tax-free/refunds", {
          params: {
            query: {
              limit: query.limit ?? 200,
              ...(status ? { status } : {}),
              ...(query.cursor ? { cursor: query.cursor } : {}),
            },
          },
          ...withSignal(signal),
        }),
      ),
    customsResult: (id, body, key) =>
      unwrap(
        tax.POST("/v1/admin/tax-free/records/{record_id}/customs-result", {
          params: { path: { record_id: id }, header: idem(key) },
          body,
        }),
      ),
    refund: (id, method, key) =>
      unwrap(
        tax.POST("/v1/admin/tax-free/records/{record_id}/refund", {
          params: { path: { record_id: id }, header: idem(key) },
          body: { method },
        }),
      ),
    writeOff: (id, reason, key) =>
      unwrap(
        tax.POST("/v1/admin/tax-free/records/{record_id}/write-off", {
          params: { path: { record_id: id }, header: idem(key) },
          body: { reason },
        }),
      ),
    bankDetails: (id, details, key) =>
      unwrap(
        tax.PUT("/v1/admin/tax-free/records/{record_id}/refund-bank-details", {
          params: { path: { record_id: id }, header: idem(key) },
          body: { bankDetails: details },
        }),
      ),
    retransmit: (id, key) =>
      unwrap(
        tax.POST("/v1/pos/tax-free/records/{record_id}/retransmit", {
          params: { path: { record_id: id }, header: idem(key) },
        }),
      ),
    cancelAndResend: (id, body, key) =>
      unwrap(
        tax.POST("/v1/pos/tax-free/records/{record_id}/cancel-and-resend", {
          params: { path: { record_id: id }, header: idem(key) },
          body,
        }),
      ),
    exports: async (signal) =>
      (
        await unwrap(
          tax.GET("/v1/admin/tax-free/exports", {
            params: { query: { limit: 50 } },
            ...withSignal(signal),
          }),
        )
      ).data,
    createExport: (body, key) =>
      unwrap(tax.POST("/v1/admin/tax-free/exports", { params: { header: idem(key) }, body })),
    downloadExport: (id, file) =>
      blob(
        tax.GET("/v1/admin/tax-free/exports/{batch_id}/download", {
          params: { path: { batch_id: id }, query: { file } },
          parseAs: "blob",
        }),
      ),
    markSubmitted: (id, body, key) =>
      unwrap(
        tax.POST("/v1/admin/tax-free/exports/{batch_id}/mark-submitted", {
          params: { path: { batch_id: id }, header: idem(key) },
          body,
        }),
      ),
    importCustoms: (csv, dryRun, key) =>
      unwrap(
        tax.POST("/v1/admin/tax-free/customs-results/import", {
          params: { query: { dryRun }, header: idem(key) },
          // the file itself as text/csv (the service also takes multipart)
          body: csv as never,
          bodySerializer: (body: unknown) => String(body),
          headers: { "Content-Type": "text/csv; charset=utf-8" },
        }),
      ),
    monthly: (month, signal) =>
      unwrap(
        tax.GET("/v1/admin/tax-free/reports/monthly", {
          params: { query: { month, format: "json" } },
          ...withSignal(signal),
        }),
      ) as Promise<TaxFreeMonthly>,
    monthlyCsv: (month) =>
      blob(
        tax.GET("/v1/admin/tax-free/reports/monthly", {
          params: { query: { month, format: "csv" } },
          parseAs: "blob",
        }),
      ),
    monthlyPdf: async (month, locale, key) => {
      const document = await unwrap(
        media.POST("/v1/admin/documents/tax-free-monthly-reports", {
          headers: idem(key),
          body: { month, locale },
        }),
      );
      const headers = new Headers({ Accept: "application/pdf" });
      const token = await options.getAccessToken?.();
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      // 302 → a short presigned link the browser follows (the PDF bytes stay in this tab)
      const response = await fetcher(
        new Request(`${root}/v1/admin/documents/${encodeURIComponent(document.id)}/download`, {
          headers,
        }),
      );
      if (!response.ok) {
        throw apiErrorFrom(response, await response.json().catch(() => undefined));
      }
      return { document, pdf: await response.blob() };
    },

    evidence: (orderId, signal) =>
      unwrap(
        tax.GET("/v1/admin/tax/export-evidence/{order_id}", {
          params: { path: { order_id: orderId } },
          ...withSignal(signal),
        }),
      ),
    evidenceQueue: (query, signal) =>
      unwrap(
        tax.GET("/v1/admin/tax/export-evidence", {
          params: {
            query: {
              limit: query.limit ?? 100,
              ...(query.status ? { status: query.status } : {}),
              ...(typeof query.olderThanDays === "number"
                ? { olderThanDays: query.olderThanDays }
                : {}),
              ...(query.cursor ? { cursor: query.cursor } : {}),
            },
          },
          ...withSignal(signal),
        }),
      ),
    addEvidence: (orderId, body, key) =>
      unwrap(
        tax.POST("/v1/admin/tax/export-evidence/{order_id}/items", {
          params: { path: { order_id: orderId }, header: idem(key) },
          body,
        }),
      ),
    removeEvidence: (orderId, itemId, key) =>
      unwrap(
        tax.DELETE("/v1/admin/tax/export-evidence/{order_id}/items/{item_id}", {
          params: { path: { order_id: orderId, item_id: itemId }, header: idem(key) },
        }),
      ),
  };
}

export function useTaxAdminApi(): TaxAdminApi {
  const options = useClientOptions();
  return useMemo(() => createTaxAdminApi(options), [options]);
}

export const TAX_KEY = ["tax"] as const;
export const TAX_SETTINGS_KEY = [...TAX_KEY, "settings"] as const;
export const TAX_CALENDAR_KEY = [...TAX_KEY, "calendar"] as const;
export const TAX_RULES_KEY = [...TAX_KEY, "rules"] as const;
export const TAX_FREE_KEY = [...TAX_KEY, "tax-free"] as const;
export const EVIDENCE_KEY = [...TAX_KEY, "export-evidence"] as const;
